/**
 * @fileoverview
 * Generic Service - Manages generic master data (availability, status, etc.)
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, map, of } from 'rxjs';
import { ApiService } from '../http';
import { API_ENDPOINTS } from '../constants';

export interface Generic {
  id: number;
  uuid: string;
  code: string;
  label: string;
  type: string;
  active: boolean;
}

export interface GenericDropdownOption {
  value: string;
  label: string;
  id: number;
  code: string;
  type: string;
}

@Injectable({
  providedIn: 'root'
})
export class GenericService {
  private api = inject(ApiService);

  // Cache signals
  private _generics = signal<GenericDropdownOption[]>([]);
  private _loading = signal<boolean>(false);
  private _error = signal<string | null>(null);

  // Public readonly signals
  readonly generics = this._generics.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  /**
   * Get all generics for dropdown with caching
   */
  getGenericsForDropdown(): Observable<{ data: GenericDropdownOption[] }> {
    // Return cached data if available
    if (this._generics().length > 0) {
      return of({ data: this._generics() });
    }

    this._loading.set(true);
    this._error.set(null);

    return this.api.get<{ data: Generic[] }>(API_ENDPOINTS.GENERICS.DROPDOWN).pipe(
      map(response => {
        const dropdownOptions = (response.data || []).map(generic => ({
          value: generic.uuid,
          label: generic.label,
          id: generic.id,
          code: generic.code,
          type: generic.type,
        }));
        this._generics.set(dropdownOptions);
        this._loading.set(false);
        return { data: dropdownOptions };
      }),
      tap({
        error: (err) => {
          this._error.set(err.message || 'Failed to load generic options');
          this._loading.set(false);
        }
      })
    );
  }

  /**
   * Get generic values by slug
   */
  getGenericValuesBySlug(slug: string): Observable<GenericDropdownOption[]> {
    return this.api.get<{ data: GenericDropdownOption[] }>(
      API_ENDPOINTS.GENERICS.VALUES_BY_SLUG(slug)
    ).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Get availability options
   */
  getAvailabilityOptions(): Observable<GenericDropdownOption[]> {
    return this.getGenericValuesBySlug('availability');
  }

  /**
   * Get status options
   */
  getStatusOptions(): Observable<GenericDropdownOption[]> {
    return this.getGenericValuesBySlug('status');
  }

  /**
   * Get asset category options
   */
  getAssetCategoryOptions(): Observable<GenericDropdownOption[]> {
    return this.getGenericValuesBySlug('asset-category');
  }


  /**
   * Clear cache (useful when generics are updated)
   */
  clearCache(): void {
    this._generics.set([]);
  }
}
